
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks'
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { RootState } from '@/store';
import { updateBasicInfo, setIsDraft } from '@/store/slices/turfSlice';
import { registerNewVenue, venueUpdateById, getStaticConfig } from '@/store/thunkMidddleware/turfMiddleware';
import { ButtonWithLoader } from '../ui/buttonwithloader';
import Strings from '@/constants/String';
import { useNavigate, useLocation } from 'react-router-dom';
import routes from '@/routes/routes.json';
import { toast as newToast } from "@/components/ui/sonner";

interface BasicTurfInfoFormProps {
  onNext: () => void;
}

const BasicTurfInfoForm: React.FC<BasicTurfInfoFormProps> = ({ onNext }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate()
  const { vendorProfile} = useAppSelector((state: RootState) => state.profile);

  const { basicInfo, loading, currentOnboardVenueDetails, venueUpdateByIdResponse, staticConfigResponse } = useAppSelector((state: RootState) => state.turf);
  const { toast } = useToast();

  const [localData, setLocalData] = useState(basicInfo);
  const [isFormInputChanged, setIsFormInputChanged] = useState(false)



  useEffect(() => {
    setLocalData(basicInfo)
  }, [basicInfo])

  useEffect(()=>{
    if(!vendorProfile?.payload?.vendorProfileCompleted){
      navigate(routes.dashboard)
    }
  },[])

  useEffect(() => {
    dispatch(getStaticConfig({ pathparams: Strings.staticConfigKeys.amenities }))

  }, [])
  const handleInputChange = (field: string, value: string | number) => {
    setIsFormInputChanged(true)
    const updatedData = { ...localData, [field]: value };
    setLocalData(updatedData);
    dispatch(updateBasicInfo({ [field]: value }));
  };

  const handleAmenityChange = (amenity: string, checked: boolean) => {
    setIsFormInputChanged(true)
    const updatedAmenities = checked
      ? [...localData.amenities, amenity]
      : localData.amenities.filter(a => a !== amenity);

    const updatedData = { ...localData, amenities: updatedAmenities };
    setLocalData(updatedData);
    dispatch(updateBasicInfo({ amenities: updatedAmenities }));
  };

  const validateForm = (): boolean => {
    if (!localData.name.trim()) {
      newToast.error('Venue name is required', { closeButton: true });
      return false;
    }

    if (!localData.basePrice || localData.basePrice <= 0) {
      // toast({ title: 'Error', description: 'Please enter a valid base price', variant: 'destructive' });
      newToast.error('Please enter a valid base price', { closeButton: true });

      return false;
    }

    return true;
  };

  const handleSubmit = () => {

    if (!validateForm()) return;
    if (isFormInputChanged) {
      setIsFormInputChanged(false);
      if (currentOnboardVenueDetails?.payload?._id) {
        updateVenue(currentOnboardVenueDetails?.payload?._id, (res) => {
          // toast({ title: 'Success', description: 'Basic information saved!' });
          newToast.success('Basic information saved!', { closeButton: true });

          onNext();
        })
      } else {
        onboardNewVenue((res) => {
          if (res?.payload) {
            // toast({ title: 'Success', description: 'Basic information saved!' });
            newToast.success('Basic information saved!', { closeButton: true });

            navigate(`${routes.editVenue}/${res?.payload?._id}`)

          }
        })
      }
    } else {
      // toast({ title: 'Success', description: 'Nothing modified!' });
      newToast.success('Nothing modified!', { closeButton: true });

      onNext();
    }
  };

  const handleSaveAsDraft = () => {
    dispatch(setIsDraft(true));
    if (isFormInputChanged) {
      toast({ title: 'Info', description: 'Saving as draft' });
      if (currentOnboardVenueDetails?.payload?._id) {
        updateVenue(currentOnboardVenueDetails?.payload?._id, (res) => {
          toast({ title: 'Success', description: 'Saved as draft' });
        })
      } else {
        onboardNewVenue((res) => {
          toast({ title: 'Success', description: 'Saved as draft' });
        })
      }
    } else {
      toast({ title: 'Warning', description: 'Nothing modified!' });
    }

  };


  const updateVenue = (venueId: String, cb: (res: any) => void) => {
    let request = {
      name: localData.name,
      baseBookingPricePerHour: localData.basePrice,
      description: localData.description,
      amenities: localData.amenities
    }
    dispatch(venueUpdateById({
      payload: request, venueId: venueId, callback: (res) => {
        console.log("venueUpdateById:", res)
        if (cb) {
          cb(res)
        }
      }
    }))
  }
  const onboardNewVenue = (cb: (res: any) => void) => {
    let request = {
      name: localData.name,
      baseBookingPricePerHour: localData.basePrice,
      description: localData.description,
      amenities: localData.amenities
    }
    dispatch(registerNewVenue({
      payload: request, callback: (res) => {
        console.log("registerNewVenue:", res)
        if (cb) {
          cb(res)
        }
      }
    }))
    // dispatch(registerNewVenue({payload:request,callback:(res)=>{

    // }}))
  }

  return (
    <div className="flex items-start justify-center">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Basic Turf Information</CardTitle>
          <CardDescription>Tell us about your turf facility</CardDescription>
          {currentOnboardVenueDetails?.payload?._id && <CardDescription >#BZY-<span className='text-flup-info'>{currentOnboardVenueDetails?.payload?._id}</span></CardDescription>}

        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Venue Name *</Label>
            <Input
              id="name"
              value={localData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter venue name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="basePrice">Base Booking Price Per Hour *</Label>
            <Input
              id="basePrice"
              type="number"
              value={localData.basePrice}
              onChange={(e) => handleInputChange('basePrice', parseFloat(e.target.value) || '')}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              placeholder="Enter base price in ₹"
              min="0"
              step="50"
            />
            <p className="text-sm text-flup-ink-3">This will be the default price, you can set specific prices for each court later</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={localData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe your turf facility..."
              rows={4}
            />
          </div>

          <div className="space-y-4">
            <Label>Amenities</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {staticConfigResponse?.payload?.key === Strings.staticConfigKeys.amenities && staticConfigResponse?.payload?.values.map((amenity) => (
                <div key={amenity} className="flex items-center space-x-2">
                  <Checkbox
                    id={amenity}
                    checked={localData.amenities.includes(amenity)}
                    onCheckedChange={(checked) => handleAmenityChange(amenity, checked as boolean)}
                  />
                  <Label htmlFor={amenity} className="text-sm cursor-pointer">
                    {amenity}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              variant="outline"
              onClick={handleSaveAsDraft}
              className="flex-1"
              disabled={venueUpdateByIdResponse.isLoading || loading}

            >
              Save as Draft
            </Button>
            <ButtonWithLoader
              loaderPosition='right'
              isLoading={venueUpdateByIdResponse.isLoading || loading}
              onClick={handleSubmit}
              className="flex-1 bg-flup-brand hover:bg-flup-brand-press text-white"
              disabled={venueUpdateByIdResponse.isLoading || loading}
            >
              Continue
            </ButtonWithLoader>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BasicTurfInfoForm;
